// Put a panel back at the top of the viewport after a save.
//
// Saving goes through the app's global spinner (a Bootstrap Modal), and while
// that modal is up the body can't scroll — a window.scrollTo issued underneath
// it is clamped to 0, which is why "restore the Y we captured" left the report
// scrolled to the very top instead of where the practitioner was editing. And
// the modal closing on top of a layout that just shrank (a tall editor became a
// shorter preview) drops the page at the top on its own. So: scroll only AFTER
// the save has resolved (spinner gone), and anchor on the panel's own element
// rather than a captured Y, since the panel is a different height by then.
//
// The sticky header (.App-sticky-top) sits over the top of the page, so offset
// by its height — same pattern as scrollComparisonTitleIntoView in the
// comparison report.
export function scrollPanelIntoView(
  target: string | HTMLElement | null,
): void {
  const scroll = () => {
    const el =
      typeof target === 'string' ? document.getElementById(target) : target;
    if (!el || typeof window.scrollTo !== 'function') return;
    const top = el.getBoundingClientRect().top + window.scrollY - stickyHeight();
    window.scrollTo({ top: Math.max(top, 0) });
  };
  // Once on the next frame, then again once the spinner modal has definitely
  // unmounted and released the body scroll lock.
  requestAnimationFrame(scroll);
  window.setTimeout(scroll, 250);
}

/** What the sticky header covers at the top of the page. */
function stickyHeight(): number {
  const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
  return sticky ? sticky.getBoundingClientRect().height : 0;
}

/** Within a couple of pixels is on target; sub-pixel layout is not worth chasing. */
const TOLERANCE = 2;

/** Whether the panel is sitting under the sticky header already. */
function panelIsInPlace(el: HTMLElement): boolean {
  const gap = el.getBoundingClientRect().top - stickyHeight();
  if (Math.abs(gap) <= TOLERANCE) {
    return true;
  }
  // A scroll can never take an element past the bottom of the document, so on a
  // page with little room under the target, as far down as the page goes IS the
  // landing place. Without this, such a target would retry to the cap every
  // time and hold the page against the practitioner while it did.
  const maxScroll = Math.max(
    document.documentElement.scrollHeight - window.innerHeight,
    0,
  );
  return gap > 0 && window.scrollY >= maxScroll - TOLERANCE;
}

/**
 * Like scrollPanelIntoView, but keeps asking until the page actually moves.
 *
 * For the caller whose own action is what puts the spinner up, rather than one
 * scrolling after a save has finished. A scroll issued while ANY modal is
 * mounted goes nowhere — react-overlays sets overflow:hidden on the body for as
 * long as one is up, so the viewport has nothing to scroll — and the spinner
 * stays up for however long the write takes. That is why naming a new section
 * mid-entry moved the page only sometimes: typing a name already on the
 * practitioner's list saves nothing, so nothing was in the way, while a
 * genuinely new one saved the profile and the scroll was swallowed.
 *
 * So: try, and if the page did not move, try again until it does. Capped, at
 * which point the write has plainly outlasted anything worth following the
 * practitioner around for.
 */
export function scrollPanelIntoViewUntilItLands(
  id: string,
  attempts: number = 30,
): void {
  const attempt = (left: number) => {
    if (typeof window.scrollTo !== 'function') return;
    // A panel that is not drawn yet is waited for on the same terms as a page
    // that cannot scroll yet: both are "not ready", and the caller's whole
    // reason for being here is that something else is still finishing.
    const el = document.getElementById(id);
    if (el) {
      if (panelIsInPlace(el)) return;
      const top =
        el.getBoundingClientRect().top + window.scrollY - stickyHeight();
      window.scrollTo({ top: Math.max(top, 0) });
      // Read back rather than assumed: an instant scroll has already happened
      // by the time this line runs, so the position is the answer to "did
      // anything let go of the page?".
      if (panelIsInPlace(el)) return;
    }
    if (left <= 0) return;
    window.setTimeout(() => attempt(left - 1), 100);
  };
  // The panel is often drawn by the same batch of state that asked for this
  // scroll, so let React have its frame first.
  requestAnimationFrame(() => attempt(attempts));
}
